import '../css/variables.scss'
import '../css/global.scss'
import '../css/scaffold.scss'
import './bootstrap'
import '../css/app.css'
import { createApp, h, watch } from 'vue'
import { createInertiaApp, router, usePage } from '@inertiajs/vue3'
import {
    handleVitePreloadError,
    isReloadingForStaleChunk,
} from '@/HelperFunctions/vitePreloadError'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import LaravelPermissionToVueJS from 'laravel-permission-to-vuejs'
import { ZiggyVue } from 'ziggy-js'
import vSelect from 'vue-select'
import { createPinia } from 'pinia'
import 'vue-select/dist/vue-select.css'
import { modal } from '@/vendor/inertia-modal.js'
import '@vuepic/vue-datepicker/dist/main.css'
import { i18nVue, loadLanguageAsync } from 'laravel-vue-i18n'
import { PageProps } from '@/Models/Inertia'
import { isFileDrag } from '@/HelperFunctions'
import { useModalStack } from './useModalStack'
import { useToast } from '@/Composables/useToast'
import { syncPresenceHeartbeat } from '@/Composables/usePresenceHeartbeat'
import * as Sentry from '@sentry/vue'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

let title = import.meta.env.VITE_APP_NAME || 'Laravel'
const appName = title.replace(/_/g, ' ')
const page = usePage<PageProps>()

// Glob eenmalig opbouwen — niet per props-change herevalueren. Vite splitst zo
// elke whitelabel SCSS in een aparte chunk; we laden alleen die van het actieve
// theme. Voorheen werd dit binnen de watch callback opnieuw geëvalueerd op elke
// prop wijziging (Inertia navigatie) — onnodig werk + onnodige bundle inflation.
const themeLoaders = import.meta.glob('../css/white-label/*-variables.scss')
let loadedTheme: string | null = null

watch(
    () => page.props,
    (props) => {
        if (props?.theme && props.theme !== loadedTheme) {
            const theme = props.theme as string
            const themePath = `../css/white-label/${theme}-variables.scss`
            const loader = themeLoaders[themePath]
            if (loader) {
                // Pas `loadedTheme` zetten ná succesvolle load, zodat een
                // gefaalde import niet permanent re-imports blokkeert.
                loader()
                    .then(() => {
                        loadedTheme = theme
                    })
                    .catch((e) => console.error('Theme load failed', theme, e))
            }
        }
        if (props && props.locale) {
            loadLanguageAsync(props.locale as string)
        }
    },
    { immediate: true }
)

// #6222: het dialect komt uit de Inertia-props (app_details.locale_dialect),
// bepaald per whitelabel-host. Enkel toepassen als het dialect bij de gevraagde
// taal hoort (fr-FR ↔ fr); bij een taalwissel naar bv. nl blijft alles vanilla.
const dialectFor = (lang: string): string | null => {
    const dialect = page.props?.app_details?.locale_dialect ?? null
    if (!dialect || dialect.slice(0, 2).toLowerCase() !== lang.slice(0, 2).toLowerCase()) {
        return null
    }
    return dialect
}

// Vite levert JSON-imports als module (`{ default: {...} }`); haal de inhoud eruit.
const unwrapModule = (m: unknown): Record<string, unknown> => {
    const mod = m as Record<string, unknown>
    return (
        mod && typeof mod.default === 'object' && mod.default !== null ? mod.default : mod
    ) as Record<string, unknown>
}

// De php_*.json-bestanden zijn plat (dot-keys → string); geneste objecten
// worden recursief samengevoegd, de overlay wint op sleutelniveau.
const mergeTranslations = (
    b: Record<string, unknown>,
    o: Record<string, unknown>
): Record<string, unknown> => {
    const out: Record<string, unknown> = { ...b }
    for (const [k, v] of Object.entries(o)) {
        const cur = out[k]
        out[k] =
            v && typeof v === 'object' && cur && typeof cur === 'object'
                ? mergeTranslations(cur as Record<string, unknown>, v as Record<string, unknown>)
                : v
    }
    return out
}

// Create persistent modal container
const createPersistentModalContainer = () => {
    if (document.getElementById('persistent-modal-container')) return

    const container = document.createElement('div')
    container.id = 'persistent-modal-container'
    container.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 1001;
        pointer-events: none;
        transition: none;
        display: none;
    `

    const backdrop = document.createElement('div')
    backdrop.id = 'persistent-backdrop'
    backdrop.style.cssText = `
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.15s ease-in-out;
        pointer-events: auto;
    `

    container.appendChild(backdrop)
    document.body.appendChild(container)
}

// Initialize immediately - don't wait for DOMContentLoaded
createPersistentModalContainer()

// Also initialize when DOM is ready as fallback
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createPersistentModalContainer)
}

export { createPersistentModalContainer }

/**
 * Globale vangnet voor bestanden die náást een uploadveld worden losgelaten (#6653).
 *
 * Standaard navigeert de browser bij een drop van een bestand naar dat bestand
 * (`file:///…pdf`), waardoor de hele app — en alle niet-opgeslagen invoer — verdwijnt.
 * Gemeld door Sage (Freshdesk 13439): op /costs/upload opende Chrome bij een tweede
 * drop de pdf in de tab en was het formulier weg.
 *
 * Bewust beperkt tot drags met bestanden: tekst slepen binnen een textarea of tussen
 * velden blijft native werken. Een native `<input type="file">` slaan we over, want
 * daar hoort de browser de drop zelf af te handelen.
 *
 * Zie: #6653, https://numnum.freshdesk.com/a/tickets/13439
 */
const isNativeFileInput = (target: EventTarget | null): boolean =>
    target instanceof HTMLInputElement && target.type === 'file'

const blockStrayFileDrop = (event: DragEvent) => {
    if (!isFileDrag(event) || isNativeFileInput(event.target)) {
        return
    }

    // Vóór onze eigen preventDefault() lezen: een dropzone (VFileDropzone) die het event al
    // afhandelde heeft dat gezet en daar hoort de 'copy'-cursor te blijven staan.
    const handledByDropzone = event.defaultPrevented

    event.preventDefault()

    // Enkel op dragover: toont de "niet toegestaan"-cursor buiten een dropzone.
    if (event.type === 'dragover' && event.dataTransfer && !handledByDropzone) {
        event.dataTransfer.dropEffect = 'none'
    }
}

window.addEventListener('dragover', blockStrayFileDrop)
window.addEventListener('drop', blockStrayFileDrop)

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const { getStackCount, tryCloseTopModal } = useModalStack() // <-- Get stack functions

        let hideTimeout: NodeJS.Timeout | null = null

        watch(
            getStackCount,
            (count) => {
                const container = document.getElementById('persistent-modal-container')
                const backdrop = document.getElementById('persistent-backdrop')
                if (!container || !backdrop) return

                if (hideTimeout) {
                    clearTimeout(hideTimeout)
                    hideTimeout = null
                }

                if (count > 0) {
                    container.style.display = 'block'
                    container.style.pointerEvents = 'auto'
                    // <-- MODIFIED: Use a fixed background/opacity for the main backdrop
                    backdrop.style.background = `rgba(0, 0, 0, 0.5)`
                    requestAnimationFrame(() => {
                        backdrop.style.opacity = '1'
                    })
                    document.body.style.overflow = 'hidden'
                } else {
                    hideTimeout = setTimeout(() => {
                        backdrop.style.opacity = '0'
                        document.body.style.overflow = null
                        setTimeout(() => {
                            if (getStackCount.value === 0) {
                                backdrop.style.background = `rgba(0, 0, 0, 0.5)`
                                container.style.pointerEvents = 'none'
                                container.style.display = 'none'
                            }
                        }, 150)
                        hideTimeout = null
                    }, 50)
                }
            },
            { immediate: true }
        )

        // Add ONE backdrop click listener
        const setupBackdropListener = () => {
            const backdrop = document.getElementById('persistent-backdrop')
            if (backdrop) {
                backdrop.onclick = () => tryCloseTopModal('backdrop') // Use onclick to easily replace
            }
        }

        // Add ONE Escape key listener
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                tryCloseTopModal('escape')
            }
        })

        // Ensure listeners are set up after DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', setupBackdropListener)
        } else {
            setupBackdropListener()
        }

        const pinia = createPinia()
        pinia.use(piniaPluginPersistedstate)

        const numnumApp = createApp({ render: () => h(App, props) })
            .use(modal, {
                resolve: (name: string) =>
                    resolvePageComponent(
                        `./Pages/${name}.vue`,
                        import.meta.glob('./Pages/**/*.vue')
                    ),
            })
            .use(plugin)
            .use(ZiggyVue)
            // i18n
            .use(i18nVue, {
                resolve: async (lang: string) => {
                    const langs = import.meta.glob('../../lang/*.json')
                    const base = await langs[`../../lang/php_${lang}.json`]()

                    // #6222: whitelabel-dialect als overlay (bv. fr-FR bovenop fr).
                    // De laravel-vue-i18n Vite-plugin genereert per map onder lang/
                    // een eigen php_<map>.json maar merget NIET met de basistaal;
                    // lang/fr-FR bevat enkel afwijkende keys, dus zonder client-side
                    // merge zouden alle andere keys leeg zijn. Backend doet hetzelfde
                    // via Lang::determineLocalesUsing() (AppServiceProvider).
                    const dialect = dialectFor(lang)
                    const overlayLoader = dialect
                        ? langs[`../../lang/php_${dialect}.json`]
                        : undefined
                    if (!overlayLoader) {
                        return base
                    }
                    try {
                        // laravel-vue-i18n verwacht een module-vorm `{ default: messages }`.
                        return {
                            default: mergeTranslations(
                                unwrapModule(base),
                                unwrapModule(await overlayLoader())
                            ),
                        }
                    } catch (e) {
                        // Ontbrekend/onleesbaar dialect-bestand → gewoon de basistaal.
                        console.error('Dialect load failed', dialect, e)
                        return base
                    }
                },
            })
            .use(LaravelPermissionToVueJS)
            .use(pinia)
            .component('v-select', vSelect)

        if (
            import.meta.env.VITE_SENTRY_DSN &&
            !['development', 'local'].includes(import.meta.env.VITE_APP_ENV)
        ) {
            // import.meta.env.ENV bestaat niet (Vite geeft enkel VITE_-variabelen door), waardoor
            // staging als "production" in Sentry belandde. app_env is null op productie.
            const sentryEnvironment =
                (props.initialPage.props.app_env as string | null | undefined) ?? 'production'

            // Tracing (spans) staat los van errors: elke error blijft binnenkomen, ook als de
            // pageload of navigatie niet gesampled is. Met tracesSampleRate 1.0 stuurde elke
            // paginalading en navigatie een transactie met tientallen spans (o.a. resources:
            // elk JS-chunk, CSS-bestand en afbeelding). Dat verbruikte het maandquotum van
            // 5M spans in enkele dagen (Sentry-mail 14/10/2026: 80% na 3 dagen). We gebruiken
            // Sentry vooral om bugs na releases op te sporen, dus een kleine steekproef op
            // productie volstaat; staging traceert niet. Overschrijfbaar via
            // VITE_SENTRY_TRACES_SAMPLE_RATE in de .env van de build.
            const configuredTracesSampleRate = Number.parseFloat(
                import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? ''
            )
            const tracesSampleRate = Number.isFinite(configuredTracesSampleRate)
                ? configuredTracesSampleRate
                : sentryEnvironment === 'production'
                  ? 0.05
                  : 0

            Sentry.init({
                app: numnumApp,
                dsn: import.meta.env.VITE_SENTRY_DSN,
                release: import.meta.env.VITE_SENTRY_RELEASE,
                environment: sentryEnvironment,
                // Setting this option to true will send default PII data to Sentry.
                // For example, automatic IP address collection on events
                sendDefaultPii: true,
                integrations: [
                    Sentry.browserTracingIntegration({
                        // Resource-spans (scripts, CSS, afbeeldingen, fonts) zijn talrijk per
                        // paginalading en helpen niet bij het opsporen van bugs.
                        ignoreResourceSpans: [
                            'resource.script',
                            'resource.css',
                            'resource.link',
                            'resource.img',
                            'resource.other',
                        ],
                        // Long tasks zijn performance-signalen (trage UI), geen bugs.
                        enableLongTask: false,
                        // De presence-heartbeat en de token-keepalive lopen per open tab de hele
                        // dag door en zeggen niets over bugs, dus geen spans voor die requests.
                        shouldCreateSpanForRequest: (url) =>
                            !/\/presence\/|\/validate-token-web/.test(url),
                    }),
                    // Sentry.replayIntegration({
                    //     maskAllText: false,
                    //     blockAllMedia: false,
                    // })
                ],
                // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                // replaysSessionSampleRate: 0.1,
                // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
                // replaysOnErrorSampleRate: 1.0,
                // Tracing
                tracesSampleRate,
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

                // Negeer errors die afkomstig zijn van externe scripts (bijv. GTM-tags zoals ping.js).
                // Deze scripts draaien buiten onze codebase en genereren SecurityErrors in
                // browsers met strict privacy mode (Firefox, Safari ITP, incognito).
                denyUrls: [/\/ping\.js/],

                // Negeer bekende browser extension errors die niet afkomstig zijn van onze codebase.
                // "runtime.sendMessage Tab not found" is een Chrome extension API fout die optreedt
                // wanneer een extensie probeert te communiceren met een tab die niet meer bestaat.
                // Dit is niet actionable vanuit onze kant. Sentry issue: 80814275
                ignoreErrors: [
                    /Invalid call to runtime\.sendMessage\(\)/,
                    /Object Not Found Matching Id:\d+, MethodName:\w+/,
                ],

                // Before submitting, set user and additional context about the current session
                beforeSend: (event, hint) => {
                    if (isReloadingForStaleChunk()) {
                        return null
                    }

                    const page = usePage<PageProps>()

                    // Filter network errors - deze zijn niet actionable en komen door
                    // instabiele verbindingen van eindgebruikers (mobiel, wifi dropout, etc.)
                    const error = hint.originalException
                    if (error instanceof Error) {
                        const errorMessage = error.message?.toLowerCase() || ''
                        const errorName = error.name?.toLowerCase() || ''

                        // Negeer generieke netwerk errors die we niet kunnen oplossen
                        if (
                            errorName === 'axioserror' &&
                            (errorMessage.includes('network error') ||
                                errorMessage.includes('timeout') ||
                                errorMessage.includes('aborted'))
                        ) {
                            return null
                        }
                    }

                    event.user = {
                        id: page.props.auth?.user?.uuid ?? null,
                        email: page.props.auth?.user?.email ?? null,
                    }

                    event.tags = {
                        ...event.tags,
                        session_entity_id: page.props.session_entity_id ?? null,
                        passport_token: page.props.passport_token ?? null,
                    }

                    return event
                },
            })
        }

        // Monitor actieve gebruikers (GH #6960): enkel voor ingelogde gebruikers,
        // op de klant- én de admin-host. Wie pas na het laden inlogt (Inertia-
        // redirect zonder herladen), wordt via het navigate-event hieronder opgepikt.
        syncPresenceHeartbeat(!!(props.initialPage.props as Partial<PageProps>)?.auth?.user)

        return numnumApp.mount(el)
    },
    progress: {
        delay: 250,
        color: '#29d',
        includeCSS: true,
        showSpinner: false,
    },
})

// ─── #5628: navigatie-fouten niet stil laten falen ──────────────────────────
// Symptoom uit het ticket: "menu staat er, maar klikken op een item doet niks, de
// pagina blijft op /dashboard" — zonder toast of foutmodal. Twee oorzaken vallen
// vandaag allebei stil weg:
//   H2) stale Vite-chunk na een deploy: de gebruiker draait nog op een oude build,
//       de dynamische import van de doel-pagina 404't (hash bestaat niet meer) en
//       de Inertia-navigatie breekt stil af.
//   H1) de backend weigert de nav-visit (MenuAccessMiddleware abort 418/419) → dat
//       komt als non-Inertia response binnen → standaard een rauwe error-modal, en
//       de axios-interceptor in bootstrap.ts slaat Inertia-visits juist bewust over.
// We herstellen H2 automatisch en geven H1 zichtbare feedback. Beide paden taggen
// naar Sentry zodat de eerstvolgende echte melding H1 vs H2 eenduidig maakt.
// H2: zie handleVitePreloadError() voor de reload en de loopguard.
window.addEventListener('vite:preloadError', (event) =>
    handleVitePreloadError(event, {
        storage: sessionStorage,
        reload: () => window.location.reload(),
        report: (payload) =>
            Sentry.captureMessage('Navigation blocked: vite chunk preload error', {
                level: 'warning',
                tags: { nav_blocked: 'true', nav_blocked_reason: 'chunk_preload_error' },
                extra: { module: String(payload ?? '') },
            }),
    })
)

// H1: bij een geweigerde nav-visit een nette toast tonen i.p.v. de rauwe Inertia
// error-modal. Hergebruikt dezelfde keys als bootstrap.ts (418 → permissie, 419 →
// licentie). 403 laten we met rust: dat komt uit policies/throttles met eigen UX.
router.on('invalid', (event) => {
    const status = (event.detail.response as { status?: number } | undefined)?.status
    if (status !== 418 && status !== 419) {
        return
    }
    event.preventDefault()

    Sentry.captureMessage(`Navigation blocked: permission denial (${status})`, {
        level: 'warning',
        tags: { nav_blocked: 'true', nav_blocked_reason: `inertia_invalid_${status}` },
    })

    useToast().warning(
        status === 419 ? 'errors.no_active_license' : 'errors.permission_denied_inline'
    )
})
// ─────────────────────────────────────────────────────────────────────────────

// Monitor actieve gebruikers (GH #6960): de heartbeat volgt de ingelogde
// gebruiker per navigatie, zodat een uitlog en nieuwe login zonder herladen
// hem stoppen en weer starten. syncPresenceHeartbeat() is idempotent.
router.on('navigate', (event) => {
    syncPresenceHeartbeat(!!(event.detail.page.props as Partial<PageProps>)?.auth?.user)
})

// Google Analytics pageview tracking for Inertia navigation
// Push user data to dataLayer when available
if (typeof window !== 'undefined' && (window as any).dataLayer) {
    const pushUserToDataLayer = () => {
        const page = usePage<PageProps>()
        // page.props kan undefined zijn wanneer dit buiten Inertia context draait
        // (bijv. bij initiële load voordat de app gemount is)
        if (page.props?.auth?.user) {
            ;(window as any).dataLayer.push({
                userId: page.props.auth.user.uuid,
                userEmail: page.props.auth.user.email,
            })
        }
    }

    // Push user data on initial load
    pushUserToDataLayer()

    // Track pageviews on Inertia navigation
    router.on('navigate', (event) => {
        ;(window as any).dataLayer.push({
            event: 'pageview',
            page: {
                url: event.detail.page.url,
                title: document.title,
            },
        })
    })
}
